import Alert from "@mui/material/Alert";
import { Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAddPanelsToSwitchgear } from "../data/Assets/useAddPanelToHvSwitchgear";
import { CancelButton } from "../shared/CancelButton";
import { managedSurveyAssetType, ManagedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { getSubAssetType, Panel, PanelsSubmission } from "../shared/interfaces/SurveyableAsset";
import { ProgressButton } from "../shared/ProgressButton";
import { Snackbar } from "../shared/Snackbar";
import { validPanelNumber } from "./PanelNumberRegex";

interface Props {
  switchgearId: number;
  existingPanels?: Panel[];
  errorUpdatingData?: () => void;
  visible: boolean;
  onClose: () => void;
  onSuccess: (panelNumber: string, name: string) => void;
  prepopulatedPanelDetails?: Panel;
  enableEditingPanelNo?: boolean;
  parentAssetType?: ManagedSurveyAssetType;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "nowrap",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(0),
      width: "100%",
    },
    formLabel: {
      marginRight: theme.spacing(4),
      fontSize: "14px",
    },
    formValue: {
      marginRight: theme.spacing(2),
      fontSize: "14px",
      width: "60%",
    },
    inputText: {
      width: "100%",
    },
  }),
);

export function AddSinglePanelToSwitchgearDialog(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
  } = useForm();
  const { onSuccess, switchgearId, prepopulatedPanelDetails, existingPanels } = props;

  const [panelNumber, setPanelNumber] = useState(prepopulatedPanelDetails?.panelNo || "");
  const [panelName, setPanelName] = useState(prepopulatedPanelDetails?.name || "");

  const {
    mutate: addPanelToSwitchgear,
    isError: errorAddingPanel,
    isLoading: addingPanelLoading,
    reset: resetAddPanelToSwitchgear,
  } = useAddPanelsToSwitchgear({
    onSuccess: onAddPanelSuccess,
    onError: onAddPanelError,
  });

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const subAssetLabel = getSubAssetType(props.parentAssetType || managedSurveyAssetType.Switchgear);

  function handleCloseSnackbar() {
    resetAddPanelToSwitchgear();
    setShowSnackbar(false);
  }

  useEffect(() => {
    if (!errorAddingPanel) {
      setShowSnackbar(false);
    }
  }, [errorAddingPanel]);

  function onAddPanelSuccess() {
    resetAddPanelToSwitchgear();
    onSuccess(panelNumber, panelName);
  }

  function onAddPanelError() {
    setSnackbarText(t("common.dataError", { val: t("common.createEntity", { entity: t(subAssetLabel) }) }));
    setShowSnackbar(true);
  }

  const savePanel = handleSubmit(() => {
    const panelsSubmission: PanelsSubmission = {
      surveyableAssetId: switchgearId,
      panels: [
        {
          name: panelName,
          panelNo: panelNumber,
        },
      ],
    };
    addPanelToSwitchgear(panelsSubmission);
  });

  function handlePanelNameChange(event: any) {
    setValue("panelDialog.panel_name", event.target.value, {
      shouldValidate: false,
    });
    setPanelName(event.target.value);
  }

  function handlePanelNumberChange(event: any) {
    setPanelNumber(event.target.value);
    setValue("panelDialog.panel_number", event.target.value, {
      shouldValidate: true,
    });
    clearErrors("panelDialog.panel_number");
  }

  const header = (
    <Typography variant="h5" component="p">
      {t("common.createEntity", {
        entity: t(subAssetLabel),
      })}
    </Typography>
  );

  const footer = (
    <React.Fragment>
      <ProgressButton loading={addingPanelLoading} onClick={savePanel}>
        {t("common.save")}{" "}
      </ProgressButton>
      <CancelButton onClick={props.onClose} className="m1-1" />
    </React.Fragment>
  );

  return props.visible ? (
    <>
      <Dialog
        visible={props.visible}
        header={header}
        footer={footer}
        onHide={props.onClose}
        breakpoints={{
          "2000px": "35vw",
          "1300px": "50vw",
          "900px": "75vw",
          "640px": "98vw",
        }}
        style={{ width: "20vw" }}
      >
        <div className={classes.formControl}>
          <Typography className={classes.formLabel} variant="subtitle2">
            {t("panels.panelNumberToBeRemoved", { val: subAssetLabel })}
          </Typography>
          <div className={classes.formValue}>
            {props.enableEditingPanelNo ? (
              <InputText
                className={clsx(classes.inputText, {
                  "p-invalid": errors?.panelDialog?.panel_number,
                })}
                value={panelNumber}
                placeholder={t("panels.panelNumberToBeRemoved", { val: subAssetLabel })}
                {...register("panelDialog.panel_number", {
                  value: panelNumber,
                  validate: {
                    notEmpty: (value) => value && value !== "",
                    notDuplicate: (value) => !existingPanels?.some((panel) => panel.panelNo === value),
                    correctPattern: (value) => validPanelNumber(value),
                  },
                })}
                onChange={handlePanelNumberChange}
              />
            ) : (
              <Typography align="left" className={classes.formValue}>
                {prepopulatedPanelDetails?.panelNo}
              </Typography>
            )}
            {errors?.panelDialog?.panel_number && errors.panelDialog.panel_number.type === "notEmpty" && (
              <Typography variant="caption" component="p" className="p-error pl-1">
                {t("panels.panelNumberRequired", { val: subAssetLabel })}
              </Typography>
            )}
            {errors?.panelDialog?.panel_number && errors.panelDialog.panel_number.type === "notDuplicate" && (
              <Typography variant="caption" component="div" className="p-error pl-1">
                {t("panels.panelNumberExists", { val: subAssetLabel })}
              </Typography>
            )}
            {errors?.panelDialog?.panel_number && errors.panelDialog.panel_number.type === "correctPattern" && (
              <Typography variant="caption" component="div" className="p-error pl-1">
                {t("panels.incorrectPanelNumberFormat", {
                  val: "panels.panel",
                })}
              </Typography>
            )}
          </div>
        </div>

        <div className={classes.formControl}>
          <Typography className={classes.formLabel} variant="subtitle2">
            {t("panels.panelNameToBeRemoved", { val: subAssetLabel })}
          </Typography>
          <div className={classes.formValue}>
            <InputText
              className={classes.inputText}
              value={panelName}
              placeholder={t("panels.panelNameToBeRemoved", { val: subAssetLabel })}
              {...register("panelDialog.panel_name", {
                required: false,
                value: panelName,
              })}
              onChange={handlePanelNameChange}
            />
          </div>
        </div>
      </Dialog>
      <Snackbar open={showSnackbar} onClose={handleCloseSnackbar} message={snackbarText}>
        {errorAddingPanel ? (
          <Alert severity="error" variant="filled">
            {snackbarText}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  ) : null;
}
